'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Transaction {
  id: string;
  orderId: string;
  route: string;
  amount: number;
  method: 'mpesa' | 'bank' | 'card';
  mpesaRef: string;
  status: 'escrowed' | 'released' | 'refunded' | 'disputed' | 'pending';
  type: 'payment' | 'release' | 'refund' | 'fee';
  travelerName: string;
  parcelCategory: string;
  serviceFee: number;
  travelerEarns: number;
  createdAt: string;
}

interface Earning {
  id: string;
  grossAmount: number;
  platformFee: number;
  netAmount: number;
  route: string;
  parcelCategory: string;
  status: string;
  settledAt: string | null;
  createdAt: string;
}

interface StorageBooking {
  id: string;
  totalPriceKes: number;
  status: string;
  paymentStatus: string;
  paymentMethod: string;
  startDate: string;
  endDate: string;
  createdAt: string;
}

interface BalanceSummary {
  totalSpent: number;
  totalEarned: number;
  pendingEscrow: number;
  storageSpend: number;
  mpesaBalance: number;
  bankBalance: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const statusColor: Record<string, string> = {
  released: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  escrowed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  refunded: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  disputed: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  pending: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  confirmed: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
};

const typeIcon: Record<string, string> = {
  payment: 'ArrowUpCircleIcon',
  release: 'ArrowDownCircleIcon',
  refund: 'ArrowPathIcon',
  fee: 'BanknotesIcon',
};

const methodLabel: Record<string, string> = {
  mpesa: 'M-Pesa',
  bank: 'Bank',
  card: 'Card',
};

// ─── Receipt Generator ────────────────────────────────────────────────────────
function downloadReceipt(tx: Transaction) {
  const lines = [
    '========================================',
    '         MUYURIDE PAYMENT RECEIPT        ',
    '========================================',
    `Receipt ID  : ${tx.id.slice(0, 8).toUpperCase()}`,
    `Date        : ${new Date(tx.createdAt).toLocaleString()}`,
    `Order Ref   : ${tx.orderId || 'N/A'}`,
    `Route       : ${tx.route || 'N/A'}`,
    `Category    : ${tx.parcelCategory || 'N/A'}`,
    `Traveler    : ${tx.travelerName || 'N/A'}`,
    '----------------------------------------',
    `Amount      : KES ${fmt(tx.amount)}`,
    `Service Fee : KES ${fmt(tx.serviceFee || 0)}`,
    `Method      : ${methodLabel[tx.method] || tx.method}`,
    tx.mpesaRef ? `M-Pesa Ref  : ${tx.mpesaRef}` : '',
    `Type        : ${tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}`,
    `Status      : ${tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}`,
    '========================================',
    '   Thank you for using MuyuRide!         ',
    '========================================',
  ]
    .filter(Boolean)
    .join('\n');

  const blob = new Blob([lines], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `receipt-${tx.id.slice(0, 8)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadStorageReceipt(bk: StorageBooking) {
  const lines = [
    '========================================',
    '       MUYURIDE STORAGE RECEIPT          ',
    '========================================',
    `Receipt ID  : ${bk.id.slice(0, 8).toUpperCase()}`,
    `Date        : ${new Date(bk.createdAt).toLocaleString()}`,
    `Period      : ${bk.startDate} → ${bk.endDate}`,
    `Duration    : ${bk.durationLabel || bk.status}`,
    '----------------------------------------',
    `Total       : KES ${fmt(bk.totalPriceKes)}`,
    `Method      : ${bk.paymentMethod || 'N/A'}`,
    `Pay Status  : ${bk.paymentStatus}`,
    `Booking     : ${bk.status}`,
    '========================================',
    '   Thank you for using MuyuRide!         ',
    '========================================',
  ]
    .filter(Boolean)
    .join('\n');

  const blob = new Blob([lines], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `storage-receipt-${bk.id.slice(0, 8)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Balance Card ─────────────────────────────────────────────────────────────
function BalanceCard({
  label,
  amount,
  icon,
  color,
  sub,
}: {
  label: string;
  amount: number;
  icon: string;
  color: string;
  sub?: string;
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon name={icon as any} size={20} className="text-white" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-foreground mt-0.5">KES {fmt(amount)}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
type FilterType = 'all' | 'orders' | 'storage' | 'earnings';
type StatusFilter = 'all' | 'released' | 'escrowed' | 'refunded' | 'disputed' | 'pending';

export default function UserDashboardContent() {
  const { user } = useAuth();
  const supabase = createClient();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<BalanceSummary>({
    totalSpent: 0,
    totalEarned: 0,
    pendingEscrow: 0,
    storageSpend: 0,
    mpesaBalance: 0,
    bankBalance: 0,
  });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [earnings, setEarnings] = useState<Earning[]>([]);
  const [storageBookings, setStorageBookings] = useState<StorageBooking[]>([]);

  const [filterType, setFilterType] = useState<FilterType>('all');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Transactions
      const { data: txData } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      // Traveler earnings
      const { data: earnData } = await supabase
        .from('traveler_earnings')
        .select('*')
        .eq('traveler_id', user.id)
        .order('created_at', { ascending: false });

      // Storage bookings
      const { data: sbData } = await supabase
        .from('storage_bookings')
        .select('*')
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false });

      const txList: Transaction[] = (txData || []).map((r: any) => ({
        id: r.id,
        orderId: r.order_id,
        route: r.route,
        amount: Number(r.amount),
        method: r.method,
        mpesaRef: r.mpesa_ref,
        status: r.status,
        type: r.type,
        travelerName: r.traveler_name,
        parcelCategory: r.parcel_category,
        serviceFee: Number(r.service_fee || 0),
        travelerEarns: Number(r.traveler_earns || 0),
        createdAt: r.created_at,
      }));

      const earnList: Earning[] = (earnData || []).map((r: any) => ({
        id: r.id,
        grossAmount: Number(r.gross_amount),
        platformFee: Number(r.platform_fee),
        netAmount: Number(r.net_amount),
        route: r.route,
        parcelCategory: r.parcel_category,
        status: r.status,
        settledAt: r.settled_at,
        createdAt: r.created_at,
      }));

      const sbList: StorageBooking[] = (sbData || []).map((r: any) => ({
        id: r.id,
        totalPriceKes: Number(r.total_price_kes),
        status: r.status,
        paymentStatus: r.payment_status,
        paymentMethod: r.payment_method,
        startDate: r.start_date,
        endDate: r.end_date,
        durationLabel: r.duration_type,
        createdAt: r.created_at,
      }));

      setTransactions(txList);
      setEarnings(earnList);
      setStorageBookings(sbList);

      // Compute balance summary
      const totalSpent = txList
        .filter((t) => t.type === 'payment')
        .reduce((s, t) => s + t.amount, 0);
      const totalEarned = earnList.reduce((s, e) => s + e.netAmount, 0);
      const pendingEscrow = txList
        .filter((t) => t.status === 'escrowed')
        .reduce((s, t) => s + t.amount, 0);
      const storageSpend = sbList
        .filter((b) => b.paymentStatus === 'paid')
        .reduce((s, b) => s + b.totalPriceKes, 0);
      const mpesaBalance = txList
        .filter((t) => t.method === 'mpesa' && t.type === 'release')
        .reduce((s, t) => s + t.amount, 0);
      const bankBalance = txList
        .filter((t) => t.method === 'bank' && t.type === 'release')
        .reduce((s, t) => s + t.amount, 0);

      setBalance({ totalSpent, totalEarned, pendingEscrow, storageSpend, mpesaBalance, bankBalance });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Merged & filtered history ──────────────────────────────────────────────
  type HistoryRow =
    | { kind: 'tx'; data: Transaction }
    | { kind: 'storage'; data: StorageBooking }
    | { kind: 'earning'; data: Earning };

  const allHistory: HistoryRow[] = [
    ...transactions.map((t) => ({ kind: 'tx' as const, data: t })),
    ...storageBookings.map((b) => ({ kind: 'storage' as const, data: b })),
    ...earnings.map((e) => ({ kind: 'earning' as const, data: e })),
  ].sort((a, b) => {
    const da = new Date(a.data.createdAt).getTime();
    const db = new Date(b.data.createdAt).getTime();
    return db - da;
  });

  const filtered = allHistory.filter((row) => {
    // Type filter
    if (filterType === 'orders' && row.kind !== 'tx') return false;
    if (filterType === 'storage' && row.kind !== 'storage') return false;
    if (filterType === 'earnings' && row.kind !== 'earning') return false;

    // Status filter (only for transactions)
    if (filterStatus !== 'all' && row.kind === 'tx') {
      if (row.data.status !== filterStatus) return false;
    }

    // Date range
    const rowDate = new Date(row.data.createdAt);
    if (dateFrom && rowDate < new Date(dateFrom)) return false;
    if (dateTo && rowDate > new Date(dateTo + 'T23:59:59')) return false;

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (row.kind === 'tx') {
        const t = row.data as Transaction;
        if (
          !t.route?.toLowerCase().includes(q) &&
          !t.orderId?.toLowerCase().includes(q) &&
          !t.travelerName?.toLowerCase().includes(q) &&
          !t.parcelCategory?.toLowerCase().includes(q) &&
          !t.mpesaRef?.toLowerCase().includes(q)
        )
          return false;
      } else if (row.kind === 'earning') {
        const e = row.data as Earning;
        if (!e.route?.toLowerCase().includes(q) && !e.parcelCategory?.toLowerCase().includes(q))
          return false;
      }
    }

    return true;
  });

  // ─── Earnings breakdown ──────────────────────────────────────────────────────
  const totalGross = earnings.reduce((s, e) => s + e.grossAmount, 0);
  const totalFees = earnings.reduce((s, e) => s + e.platformFee, 0);
  const totalNet = earnings.reduce((s, e) => s + e.netAmount, 0);
  const totalOrderSpend = transactions
    .filter((t) => t.type === 'payment')
    .reduce((s, t) => s + t.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Financial Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Account balances, earnings breakdown, and full transaction history
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors"
        >
          <Icon name="ArrowPathIcon" size={16} />
          Refresh
        </button>
      </div>

      {/* ── Balance Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <BalanceCard
          label="Total Spent"
          amount={balance.totalSpent}
          icon="ArrowUpCircleIcon"
          color="bg-rose-500"
          sub="All order payments"
        />
        <BalanceCard
          label="Total Earned"
          amount={balance.totalEarned}
          icon="ArrowDownCircleIcon"
          color="bg-emerald-500"
          sub="Net traveler earnings"
        />
        <BalanceCard
          label="In Escrow"
          amount={balance.pendingEscrow}
          icon="LockClosedIcon"
          color="bg-amber-500"
          sub="Awaiting release"
        />
        <BalanceCard
          label="Storage Spend"
          amount={balance.storageSpend}
          icon="BuildingStorefrontIcon"
          color="bg-violet-500"
          sub="Paid storage bookings"
        />
        <BalanceCard
          label="M-Pesa Released"
          amount={balance.mpesaBalance}
          icon="DevicePhoneMobileIcon"
          color="bg-green-600"
          sub="Via M-Pesa"
        />
        <BalanceCard
          label="Bank Released"
          amount={balance.bankBalance}
          icon="BuildingLibraryIcon"
          color="bg-sky-500"
          sub="Via bank transfer"
        />
      </div>

      {/* ── Earnings / Spending Breakdown ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Earnings breakdown */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="ChartBarIcon" size={18} className="text-emerald-500" />
            Earnings Breakdown
          </h2>
          {earnings.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No earnings recorded yet.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm text-muted-foreground">Gross Earnings</span>
                <span className="text-sm font-semibold text-foreground">KES {fmt(totalGross)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm text-muted-foreground">Platform Fees</span>
                <span className="text-sm font-semibold text-rose-500">− KES {fmt(totalFees)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-semibold text-foreground">Net Earnings</span>
                <span className="text-base font-bold text-emerald-500">KES {fmt(totalNet)}</span>
              </div>
              {/* Settled vs pending */}
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center">
                  <p className="text-xs text-muted-foreground">Settled</p>
                  <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    KES {fmt(earnings.filter((e) => e.status === 'settled').reduce((s, e) => s + e.netAmount, 0))}
                  </p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center">
                  <p className="text-xs text-muted-foreground">Pending</p>
                  <p className="text-sm font-bold text-amber-600 dark:text-amber-400">
                    KES {fmt(earnings.filter((e) => e.status === 'pending').reduce((s, e) => s + e.netAmount, 0))}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Spending breakdown */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="CreditCardIcon" size={18} className="text-rose-500" />
            Spending Breakdown
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Order Payments</span>
              <span className="text-sm font-semibold text-foreground">KES {fmt(totalOrderSpend)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Storage Bookings</span>
              <span className="text-sm font-semibold text-foreground">KES {fmt(balance.storageSpend)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Service Fees</span>
              <span className="text-sm font-semibold text-foreground">
                KES {fmt(transactions.reduce((s, t) => s + (t.serviceFee || 0), 0))}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-semibold text-foreground">Total Outflow</span>
              <span className="text-base font-bold text-rose-500">
                KES {fmt(totalOrderSpend + balance.storageSpend)}
              </span>
            </div>
            {/* By method */}
            <div className="mt-3 grid grid-cols-3 gap-2">
              {(['mpesa', 'bank', 'card'] as const).map((m) => {
                const amt = transactions
                  .filter((t) => t.method === m && t.type === 'payment')
                  .reduce((s, t) => s + t.amount, 0);
                return (
                  <div key={m} className="bg-muted rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground capitalize">{methodLabel[m]}</p>
                    <p className="text-xs font-bold text-foreground mt-0.5">KES {fmt(amt)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Transaction History ── */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {/* Filters bar */}
        <div className="p-5 border-b border-border space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <Icon name="ClipboardDocumentListIcon" size={18} className="text-primary" />
              Transaction History
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                {filtered.length}
              </span>
            </h2>
          </div>

          {/* Filter controls */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Type tabs */}
            <div className="flex gap-1 bg-muted rounded-xl p-1">
              {(['all', 'orders', 'storage', 'earnings'] as FilterType[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                    filterType === t
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Status filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}
              className="text-xs bg-muted border-0 rounded-xl px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="all">All Statuses</option>
              <option value="released">Released</option>
              <option value="escrowed">Escrowed</option>
              <option value="refunded">Refunded</option>
              <option value="disputed">Disputed</option>
              <option value="pending">Pending</option>
            </select>

            {/* Date range */}
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-xs bg-muted border-0 rounded-xl px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="From"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-xs bg-muted border-0 rounded-xl px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="To"
            />

            {/* Search */}
            <div className="relative flex-1 min-w-[180px]">
              <Icon
                name="MagnifyingGlassIcon"
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search route, ref, traveler…"
                className="w-full pl-8 pr-3 py-2 text-xs bg-muted border-0 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            {/* Clear */}
            {(filterType !== 'all' || filterStatus !== 'all' || searchQuery || dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setFilterType('all');
                  setFilterStatus('all');
                  setSearchQuery('');
                  setDateFrom('');
                  setDateTo('');
                }}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 px-2 py-2"
              >
                <Icon name="XMarkIcon" size={14} />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <Icon name="DocumentTextIcon" size={28} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No transactions found</p>
            <p className="text-xs text-muted-foreground">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Date
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Description
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Type
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Method
                  </th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Amount
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Receipt
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((row, idx) => {
                  if (row.kind === 'tx') {
                    const t = row.data as Transaction;
                    return (
                      <tr key={`tx-${t.id}`} className="hover:bg-muted/30 transition-colors">
                        <td className="px-5 py-3.5 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(t.createdAt).toLocaleDateString()}
                          <br />
                          <span className="text-[10px]">{new Date(t.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-foreground text-xs truncate max-w-[180px]">
                            {t.route || 'Order Payment'}
                          </p>
                          {t.travelerName && (
                            <p className="text-[10px] text-muted-foreground">via {t.travelerName}</p>
                          )}
                          {t.parcelCategory && (
                            <p className="text-[10px] text-muted-foreground">{t.parcelCategory}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <Icon
                              name={typeIcon[t.type] as any}
                              size={14}
                              className={
                                t.type === 'payment' ?'text-rose-500'
                                  : t.type === 'release' ?'text-emerald-500' :'text-sky-500'
                              }
                            />
                            <span className="text-xs capitalize text-foreground">{t.type}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-muted-foreground">
                          {methodLabel[t.method] || t.method}
                          {t.mpesaRef && (
                            <p className="text-[10px] font-mono text-muted-foreground">{t.mpesaRef}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <span
                            className={`text-sm font-bold ${
                              t.type === 'payment' || t.type === 'fee' ?'text-rose-500' :'text-emerald-500'
                            }`}
                          >
                            {t.type === 'payment' || t.type === 'fee' ? '−' : '+'}KES {fmt(t.amount)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              statusColor[t.status] || statusColor.pending
                            }`}
                          >
                            {t.status}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <button
                            onClick={() => downloadReceipt(t)}
                            title="Download receipt"
                            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Icon name="ArrowDownTrayIcon" size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  if (row.kind === 'storage') {
                    const b = row.data as StorageBooking;
                    return (
                      <tr key={`sb-${b.id}`} className="hover:bg-muted/30 transition-colors">
                        <td className="px-5 py-3.5 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(b.createdAt).toLocaleDateString()}
                          <br />
                          <span className="text-[10px]">{new Date(b.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-foreground text-xs">Storage Booking</p>
                          <p className="text-[10px] text-muted-foreground">
                            {b.startDate} → {b.endDate}
                          </p>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <Icon name="BuildingStorefrontIcon" size={14} className="text-violet-500" />
                            <span className="text-xs text-foreground">Storage</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-muted-foreground capitalize">
                          {b.paymentMethod || 'N/A'}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <span className="text-sm font-bold text-rose-500">
                            −KES {fmt(b.totalPriceKes)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              statusColor[b.paymentStatus] || statusColor.pending
                            }`}
                          >
                            {b.paymentStatus}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <button
                            onClick={() => downloadStorageReceipt(b)}
                            title="Download receipt"
                            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Icon name="ArrowDownTrayIcon" size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  if (row.kind === 'earning') {
                    const e = row.data as Earning;
                    return (
                      <tr key={`earn-${e.id}`} className="hover:bg-muted/30 transition-colors">
                        <td className="px-5 py-3.5 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(e.createdAt).toLocaleDateString()}
                          <br />
                          <span className="text-[10px]">{new Date(e.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-foreground text-xs truncate max-w-[180px]">
                            {e.route || 'Delivery Earning'}
                          </p>
                          {e.parcelCategory && (
                            <p className="text-[10px] text-muted-foreground">{e.parcelCategory}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <Icon name="BanknotesIcon" size={14} className="text-emerald-500" />
                            <span className="text-xs text-foreground">Earning</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-muted-foreground">
                          Net: KES {fmt(e.netAmount)}
                          <p className="text-[10px]">Fee: KES {fmt(e.platformFee)}</p>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <span className="text-sm font-bold text-emerald-500">
                            +KES {fmt(e.grossAmount)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              statusColor[e.status] || statusColor.pending
                            }`}
                          >
                            {e.status}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-center text-muted-foreground text-xs">—</td>
                      </tr>
                    );
                  }

                  return null;
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
